/**
 * HU-04 (enmienda PO 3 GATE 2, S59/S60/S62 — client-side preview): the featureGroup → jobReference
 * hierarchy caps/degrades the indexValue a jobReference may hold, and the jobReference → workflow
 * hierarchy does the same one level up. Sun enforces this server-side inside the workflow save
 * (StandardWorkflowService#resolveJobReferenceIndexValueName); this module mirrors the same rule
 * client-side so the UI shows the final state the save will produce BEFORE saving.
 *
 * Degradation is one-way: a DRAFT child forces DRAFT, a PROPOSAL child caps an APPROVED parent at
 * PROPOSAL, but children moving up never auto-promote the parent (S61: approval stays an explicit
 * action).
 */

/**
 * IndexValue objects of the featureGroups reachable from the jobReference's taskReferences.
 * Mirrors the wire mapping Sun applies to the save payload (TaskReferenceMapper): a featureGroup
 * travels once per feature, so featureGroups without features never reach (nor persist on) the
 * server and must not influence the preview either.
 *
 * @param {Object} jobReference JobReference with taskReferences[].featureGroups[]
 * @returns {Array<{id: *, name: string}>} indexValue objects of the featureGroups that will travel
 */
export const collectFeatureGroupIndexValues = (jobReference) => {
  const taskReferences = (jobReference && jobReference.taskReferences) || []
  return taskReferences
    .flatMap((taskReference) => (taskReference && taskReference.featureGroups) || [])
    .filter((featureGroup) => featureGroup && Array.isArray(featureGroup.features) && featureGroup.features.length > 0)
    .map((featureGroup) => featureGroup.indexValue)
    .filter((indexValue) => indexValue && indexValue.name)
}

/**
 * Resolves the indexValue a parent element (jobReference or workflow) must show given its
 * children's indexValues: any DRAFT child forces DRAFT; otherwise a PROPOSAL child caps an
 * APPROVED parent at PROPOSAL; otherwise the parent keeps its current indexValue. The returned
 * object is taken from the triggering child so it carries a valid {id, name} for the save payload.
 *
 * @param {{id: *, name: string}} currentIndexValue The parent's current indexValue
 * @param {Array<{id: *, name: string}>} childIndexValues The children's indexValues
 * @returns {{id: *, name: string}} The indexValue the parent must hold
 */
export const resolveDegradedIndexValue = (currentIndexValue, childIndexValues) => {
  const draft = childIndexValues.find((indexValue) => indexValue.name === 'DRAFT')
  if (draft && currentIndexValue?.name !== 'DRAFT') {
    return draft
  }
  const proposal = childIndexValues.find((indexValue) => indexValue.name === 'PROPOSAL')
  if (proposal && currentIndexValue?.name === 'APPROVED') {
    return proposal
  }
  return currentIndexValue
}

/**
 * Filters the indexValue options a parent element may be set to, given its children's
 * indexValues (S60/S62): any DRAFT child leaves only DRAFT selectable; otherwise a PROPOSAL child
 * excludes APPROVED; with every child APPROVED (or no children) all options stay available. The
 * input options are already permission-filtered server-side (indexValues/available).
 *
 * @param {Array<{id: *, name: string}>} indexValues Selectable options (permission-filtered)
 * @param {Array<{id: *, name: string}>} childIndexValues The children's indexValues
 * @returns {Array<{id: *, name: string}>} The admissible subset of options
 */
export const filterAdmissibleIndexValues = (indexValues, childIndexValues) => {
  if (childIndexValues.some((indexValue) => indexValue.name === 'DRAFT')) {
    return indexValues.filter((indexValue) => indexValue.name === 'DRAFT')
  }
  if (childIndexValues.some((indexValue) => indexValue.name === 'PROPOSAL')) {
    return indexValues.filter((indexValue) => indexValue.name !== 'APPROVED')
  }
  return indexValues
}

/**
 * IndexValues of the workflow's direct children (JOB nodes' jobReferences and WORKFLOW nodes'
 * subworkflows), used to degrade the workflow's own indexValue with the same rule.
 *
 * @param {Object} workflow Workflow with nodes[]
 * @returns {Array<{id: *, name: string}>}
 */
export const collectWorkflowChildIndexValues = (workflow) => {
  const nodes = (workflow && workflow.nodes) || []
  return nodes
    .map((node) => node.nodeType === 'JOB' ? node.jobReference?.indexValue : node.subWorkflow?.indexValue)
    .filter((indexValue) => indexValue && indexValue.name)
}
